/**
 * enumDetailsDialogStore — global open/close state for the enum-values
 * details dialog launched from outside the MetaInfo manager (declared-enum
 * column header glyph, future cell context menu). The dialog mounts once at
 * the app level via `GlobalEnumDetailsDialog` so any feature can pop it by
 * calling `openEnumDetailsDialog(…)` without prop-drilling.
 */

import { create } from 'zustand';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import type { EnumValueSource } from '@/types/electron';
import type { TableExplorerMetadataEditorProviders } from '../../../table-explorer/components/tableExplorerHostPorts';

export type EnumDetailsDialogContext = {
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  column: string;
  engine: DatabaseEngineId;
  /** Source declared at decl-time — used as the default refresh source. */
  declaredSource: EnumValueSource;
  /** Source label for analytics (`'header-glyph'`, `'cell-menu'`, …). */
  source: string;
  /** When set, opens the details dialog for a path-scoped enum declaration
   * (the column hosts the JSON; the path identifies the leaf). */
  path?: string;
  /** Present iff `path` is set. */
  pathKind?: 'scalar' | 'array';
  /**
   * Host metadata provider (Azure Table, S4 review D, D5): a lookup refresh reads the target's label column through
   * it, never through the SQL metadata IPC. Transient (never persisted); absent = every SQL engine, unchanged.
   */
  metadataEditors?: TableExplorerMetadataEditorProviders;
};

type EnumDetailsDialogState = {
  isOpen: boolean;
  context: EnumDetailsDialogContext | null;
};

type EnumDetailsDialogActions = {
  openDialog: (ctx: EnumDetailsDialogContext) => void;
  closeDialog: () => void;
};

type EnumDetailsDialogStore = EnumDetailsDialogState & EnumDetailsDialogActions;

export const useEnumDetailsDialogStore = create<EnumDetailsDialogStore>(
  (set) => ({
    isOpen: false,
    context: null,
    openDialog: (ctx) => {
      // Defer one frame so an in-flight Radix ContextMenu (or other overlay)
      // close finishes BEFORE this Dialog mounts. Mounting a Radix Dialog
      // mid-close on the menu leaves `body { pointer-events: none }` stuck.
      const open = () => set({ isOpen: true, context: ctx });
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(open);
      } else {
        setTimeout(open, 0);
      }
    },
    closeDialog: () => set({ isOpen: false }),
  }),
);

export const openEnumDetailsDialog = (ctx: EnumDetailsDialogContext) =>
  useEnumDetailsDialogStore.getState().openDialog(ctx);
